// ============================================
// COMPONENT - Formulario de apertura de mi turno de caja
// ============================================

import { useState } from 'react';
import { Button } from '@almacenes/ui';
import { useAbrirTurno, useUIStore } from '@almacenes/core';
import { MdPointOfSale } from 'react-icons/md';
import { formatMontoInput, rangoAperturaRetroactiva } from '../utils/formato';
import { esConfirmacionTurnoAntiguo } from '../utils/turnoAntiguo';
import { ConfirmarTurnoAntiguoModal } from './ConfirmarTurnoAntiguoModal';

interface FormularioAperturaProps {
  /** Se llama después de abrir el turno con éxito (además del toast). */
  onSuccess?: () => void;
  /** 'card' (default): tarjeta blanca con ícono — MiCajaPage. 'none': sin wrapper, para montar dentro de otro modal. */
  chrome?: 'card' | 'none';
}

export const FormularioApertura = ({ onSuccess, chrome = 'card' }: FormularioAperturaProps) => {
  const addToast = useUIStore((state) => state.addToast);
  const abrirTurno = useAbrirTurno();
  const [montoDisplay, setMontoDisplay] = useState('');
  const [montoRaw, setMontoRaw] = useState('');
  const [observacion, setObservacion] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const [mostrarHoraPersonalizada, setMostrarHoraPersonalizada] = useState(false);
  const [fechaHoraApertura, setFechaHoraApertura] = useState('');
  const [errorFechaHora, setErrorFechaHora] = useState<string | undefined>(undefined);
  // Mensaje de la alerta del backend (turnos abiertos hace más de 3 días); null = sin alerta pendiente
  const [mensajeTurnoAntiguo, setMensajeTurnoAntiguo] = useState<string | null>(null);

  const { min: minFechaHora, max: maxFechaHora } = rangoAperturaRetroactiva();

  const handleMontoChange = (value: string) => {
    const soloNumeros = value.replace(/\D/g, '');
    setMontoRaw(soloNumeros);
    setMontoDisplay(formatMontoInput(soloNumeros));
    if (error) setError(undefined);
  };

  const handleAbrir = async (confirmado = false) => {
    if (!montoRaw || Number(montoRaw) < 0) {
      setError('El monto inicial es obligatorio');
      return;
    }
    if (mostrarHoraPersonalizada && !fechaHoraApertura) {
      setErrorFechaHora('Indica la hora real de apertura');
      return;
    }
    try {
      await abrirTurno.mutateAsync({
        montoInicial: Number(montoRaw),
        observacion: observacion.trim() || undefined,
        fechaHoraApertura: mostrarHoraPersonalizada ? fechaHoraApertura : undefined,
        confirmado,
      });
      setMensajeTurnoAntiguo(null);
      addToast('success', 'Turno abierto exitosamente');
      onSuccess?.();
    } catch (err: any) {
      if (esConfirmacionTurnoAntiguo(err)) {
        setMensajeTurnoAntiguo(err.message);
        return;
      }
      addToast('error', err?.message || 'Error al abrir el turno');
    }
  };

  const contenido = (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium mb-2" style={{ color: '#11194F' }}>
          Monto inicial (CLP) <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
          <input
            type="text"
            inputMode="numeric"
            value={montoDisplay}
            onChange={(e) => handleMontoChange(e.target.value)}
            placeholder="15.000"
            className={`w-full pl-8 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
              error ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-blue-200'
            }`}
            disabled={abrirTurno.isPending}
          />
        </div>
        {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium mb-2" style={{ color: '#11194F' }}>
          Observación (opcional)
        </label>
        <input
          type="text"
          value={observacion}
          onChange={(e) => setObservacion(e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200"
          disabled={abrirTurno.isPending}
        />
      </div>

      <div>
        <button
          type="button"
          onClick={() => {
            setMostrarHoraPersonalizada((v) => !v);
            setErrorFechaHora(undefined);
          }}
          className="text-sm font-medium underline"
          style={{ color: '#43BFFF' }}
          disabled={abrirTurno.isPending}
        >
          {mostrarHoraPersonalizada ? 'Usar la hora actual' : '¿Olvidaste abrir la caja antes? Elige la hora real'}
        </button>
        {mostrarHoraPersonalizada && (
          <div className="mt-3">
            <label className="block text-sm font-medium mb-2" style={{ color: '#11194F' }}>
              Hora real de apertura <span className="text-red-500">*</span>
            </label>
            <input
              type="datetime-local"
              value={fechaHoraApertura}
              min={minFechaHora}
              max={maxFechaHora}
              onChange={(e) => {
                setFechaHoraApertura(e.target.value);
                if (errorFechaHora) setErrorFechaHora(undefined);
              }}
              className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                errorFechaHora ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-blue-200'
              }`}
              disabled={abrirTurno.isPending}
            />
            {errorFechaHora && <p className="text-red-500 text-sm mt-1">{errorFechaHora}</p>}
            <p className="text-gray-500 text-xs mt-1">
              Las ventas que hiciste desde esa hora se vincularán automáticamente a este turno.
            </p>
          </div>
        )}
      </div>

      <Button variant="success" onClick={() => handleAbrir()} isLoading={abrirTurno.isPending} disabled={abrirTurno.isPending} className="w-full">
        Abrir mi caja
      </Button>
    </div>
  );

  const modalConfirmacion = (
    <ConfirmarTurnoAntiguoModal
      mensaje={mensajeTurnoAntiguo}
      onConfirm={() => handleAbrir(true)}
      onCancel={() => setMensajeTurnoAntiguo(null)}
      isLoading={abrirTurno.isPending}
    />
  );

  return (
    <>
      {chrome === 'none' ? (
        contenido
      ) : (
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8">
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{ backgroundColor: '#FEF3C7' }}>
                <MdPointOfSale className="text-5xl" style={{ color: '#D97706' }} />
              </div>
            </div>
            {contenido}
          </div>
        </div>
      )}
      {modalConfirmacion}
    </>
  );
};
